/**
 * Created by Daniel Wang on 1/15/2024.
 * Description: Component for add/edit etl scheduled jobs
 * ------ maintenance history ------
 */

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { UntypedFormControl } from '@angular/forms';
import { AppState } from '../../../../redux';
import * as _lodash from 'lodash';

import { SlideSheetService } from '../../../../services/slide-sheet.service';
import { SlideSheetActionTypes } from '../../../slide-sheet/slide-sheet.model';
import { ScheduledJobs, ETLTaskName, Freq } from '../../../../tamalelibs/models/admin-page.model';
import { AdminPagesService } from '../../../../tamalelibs/services/admin-page.service';
import { AdminPageActionTypes } from '../../../../redux/actions/admin-page.actions';
import { adminSharedPageConstants } from '../../admin-page-setting.constants';
import { catchError, take } from 'rxjs/operators';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { of } from 'rxjs';
import { ToastService } from '../../../../widgets/toast/toast.service';

@Component({
    selector: 'tam-etl-scheduled-jobs-editor',
    templateUrl: './etl-scheduled-jobs-editor.component.html',
    styleUrls: ['./etl-scheduled-jobs-editor.component.scss']
})
export class ETLScheduledJobsEditorComponent implements OnInit, OnDestroy {
    @Input() data?: any;

    // #region public properties
    freqItems = Object.values(Freq);
    hourItems = [];
    isEdit = false;
    minItems = [];
    name = new UntypedFormControl('');
    scheduledJob = new ScheduledJobs();
    selectedFreq = Freq['* * ?'];
    selectedHour = '';
    selectedMin = '';
    selectedTaskName = ETLTaskName.Central_Website_Task;
    taskNameItems = Object.values(ETLTaskName);
    title = 'Create Schedule Job';
    // #endregion

    // #region private properties

    // #endregion

    // #region constructor
    constructor(
        private _adminPageService: AdminPagesService,
        private _store: Store<AppState>,
        private _slideSheetService: SlideSheetService,
        private _toast: ToastService,
    ) { }
    // #endregion

    // #region angular functions
    ngOnInit() {
        // init controls
        this._initControls();
        if (this.data) {
            this.isEdit = true;
            this.title = 'Edit Schedule Job';
            this.scheduledJob = _lodash.cloneDeep(this.data);
            this.selectedTaskName = this.scheduledJob.taskName;
            this.selectedHour = this.scheduledJob.scheduleTime.split(':')[0];
            this.selectedMin = this.scheduledJob.scheduleTime.split(':')[1];
            this.selectedFreq = this.scheduledJob.freq;
        }
    }

    ngOnDestroy(): void {
    }
    // #endregion

    // #region public functions
    onSlideSheetClose() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE
        });
    }

    /** Check the length of name. When the length of the name is valid, dispatches action to the store to create/edit object. */
    onSubmitClick() {
        this.name.setErrors(null);

        if (!this.name.value) {
            this.name.setErrors({
                required: true
            });
        } else if (this.name.value.length > 128) {
            this.name.setErrors({
                maxlength: true
            });
        } else {
            this.scheduledJob.taskName = this.selectedTaskName;
            this.scheduledJob.scheduleTime = parseInt(this.selectedHour, 0) + ' ' + (this.selectedMin.length > 1 ? this.selectedMin : '0' + this.selectedMin);
            this.scheduledJob.freq = this.selectedFreq;
            this.scheduledJob.isDefault = false;
            const etlJobConfig = this._adminPageService.convertUIDataToServerData(adminSharedPageConstants['etl-scheduled-jobs'].type, this.scheduledJob);

            this._adminPageService.checkScheduledJobName(this.name.value, this.scheduledJob.id, this.data?.name, this.isEdit).pipe(
                catchError((e: any) => {
                    const mockResult = {
                        result: {
                            message: 'false' // to match server response. boolean will be better.
                        },
                    };
                    return of(mockResult);
                })
            ).subscribe(result => {
                if (result?.message === 'false') {
                    this.name.setErrors({
                        nameDuplicated: true
                    });
                } else {
                    if (this.isEdit) {
                        etlJobConfig['ID'] = this.scheduledJob.id; // sever needed
                        this._store.dispatch({
                            type: AdminPageActionTypes.EDIT_SHARED_PAGE,
                            payload: {
                                type: adminSharedPageConstants['etl-scheduled-jobs'].type,
                                key: 'etlJobConfig',
                                item: etlJobConfig
                            }
                        });
                    } else {
                        this._store.dispatch({
                            type: AdminPageActionTypes.CREATE_SHARED_PAGE,
                            payload: {
                                type: adminSharedPageConstants['etl-scheduled-jobs'].type,
                                key: 'etlJobConfig',
                                item: etlJobConfig
                            }
                        });
                    }
                }
            });
        }
    }
    // #endregion

    // #region private functions
    private _initControls() {
        // task name items
        this._adminPageService.getData(adminSharedPageConstants['etl-tasks'].type).pipe(
            take(1),
            catchError((error) => {
                this._toast.notify({
                    style: NotificationStyles.Error,
                    message: error
                });
                return of(null);
            })
        ).subscribe(res => {
            if (res && res.length > 0) {
                this.taskNameItems = res.map(item => item.taskName);
            }
        });
        // init hour controls
        for (let i = 0; i < 24; i++) {
            const item = i < 10 ? '0' + i : i;
            this.hourItems.push(item);
        }
        this.selectedHour = '00';
        // init min controls
        for (let i = 0; i < 60; i++) {
            this.minItems.push(i.toString());
        }
        this.selectedMin = '0';
    }
    // #endregion
}
